import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import DataService from '../../../services/MisDatosService';
import { useAuthStore as useAuthStoreEroski } from '../../../store/AuthContext';
import useAPIError from '../../../hooks/useAPIError';
import { PersonalData } from '../../../types/PersonalData';
import { SemaforoHelper } from '../SemaforoHelper';
import { MisDatosHelper } from '../MisDatosHelper';
import ModalConfirmacion from '../../../components/ModalConfirmacion';
import useAPILoading from '../../../hooks/useLoading';
import { SessionChecker } from '../../../utils/SessionChecker';
import { StorageHelper } from '../../../utils/StorageHelper';
import { MisDatosConstants } from '../../../utils/MisDatosConstants';
import { ErrorCodeConstants } from '../../../utils/ErrorCodeConstants';
import IconBack from '../../../Icons/IconBack';
import IconEye from '../../../Icons/IconEye';
import { CryptoUtil } from '../../../utils/CryptoUtil';

interface Props {
  userForm: boolean;
  passForm: boolean;
  setUserForm: (userForm: boolean) => void;
  setPassForm: (passForm: boolean) => void;
  accessEmail: string;
  cargarData: () => void;
  personalData: PersonalData;
}

const AccesoForm: FC<Props> = (props) => {
  const {
    userForm,
    passForm,
    setUserForm,
    setPassForm,
    accessEmail,
    cargarData,
    personalData,
  } = props;
  const dataUser = useAuthStoreEroski();
  const { t } = useTranslation();
  const { addError, addWarning, addErrorBack } = useAPIError();
  const [load, setLoad] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState('' as string);
  const [showPswAct, setShowPswAct] = useState(false as boolean);
  const [showPswNew, setShowPswNew] = useState(false as boolean);
  const [showPswRep, setShowPswRep] = useState(false as boolean);
  const [passActual, setPassActual] = useState('' as string);
  const [passSugerida, setPassSugerida] = useState('' as string);
  const [passRep, setPassRep] = useState('' as string);
  const misDatosHelper = new MisDatosHelper();
  const [textForModalCambioEmail, setTextForModalCambioEmail] = useState(
    '' as string,
  );
  const [passwordCambiada, setPasswordCambiada] = useState<boolean>(false);
  const [emailCambiado, setEmailCambiado] = useState<boolean>(false);
  const titleCambioEmail = t(
    'datos.personaldata.modal-email-confirmacion-title',
  );
  const textCambioEmail1 = t(
    'datos.personaldata.modal-email-confirmacion-message1',
  );
  const textCambioEmail2 = t(
    'datos.personaldata.modal-email-confirmacion-message2',
  );
  const titleCambioPassword = t(
    'datos.accessdata.password-change-confirmation-title',
  );
  const textCambioPassword = t(
    'datos.accessdata.password-change-confirmation-message',
  );
  const acceptButtonText = t('entendido');
  const {
    register,
    handleSubmit,
    errors,
    setValue,
    triggerValidation,
    reset,
  } = useForm({
    mode: 'onChange',
  });
  const { setApiLoading } = useAPILoading();

  const getPassSugerida = () => {
    DataService.getPassSugerida()
      .then((response) => {
        setPassSugerida(response.data);
        setPassRep(response.data);
        setShowPswAct(true);
        setShowPswNew(true);
        setShowPswRep(true);
        setValue('psw', response.data);
        setValue('repNewPsw', response.data);
        triggerValidation('psw');
        triggerValidation('repNewPsw');
        SemaforoHelper.setSemaforo(
          response.data,
          t('global.vacio'),
          t('global.bajo'),
          t('global.medio'),
          t('global.alto'),
        );
      })
      .catch((e) => {
        console.log(e);
        addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const emptymethod = () => {};

  const cerrarSesion = () => {
    const fromApp =
      dataUser.user.fromApp === undefined ? '' : dataUser.user.fromApp;
    const isApp = dataUser.user.isApp === 'true';
    if (isApp) {
      DataService.logoutUserForBaja(fromApp);
      const expiredSessionUrl = StorageHelper.getExpiredSessionUrl() as string;
      window.location.href = expiredSessionUrl;
    } else {
      DataService.logoutUserForBaja(fromApp).then((response) => {
        if (response.data.error) {
          addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
        } else {
          setApiLoading(true);
          SessionChecker.closeSesion(null);
          window.location.reload();
        }
      });
    }
  };

  const HandleChange = (
    key: string,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'newEmail':
        setNewEmail(e.target.value);
        setValue('usuario', `${e.target.value}`);
        break;
      case 'pswActual':
        setPassActual(e.target.value);
        setValue('pswActual', `${e.target.value}`);
        break;
      case 'newPsw':
        SemaforoHelper.setSemaforo(
          e.target.value,
          t('global.vacio'),
          t('global.bajo'),
          t('global.medio'),
          t('global.alto'),
        );
        setPassSugerida(e.target.value);
        setValue('psw', `${e.target.value}`);
        break;
      case 'repNewPsw':
        setPassRep(e.target.value);
        setValue('repNewPsw', `${e.target.value}`);
        break;
    }
  };

  const validarRePass = (value: any) => {
    return value === passSugerida;
  };

  const updateEmail = () => {
    if (newEmail !== '') {
      const data = {
        emailAcceso: btoa(CryptoUtil.encryptAes(newEmail)),
        fromApp: dataUser.user.isApp === 'true' ? 'app_club' : 'web',
        tipo: 'MD',
      };
      DataService.updateEmailAcceso(data)
        .then((response) => {
          if (response.data.error === true) {
            if (
              response.data.errorCode ===
              ErrorCodeConstants.UPDATE_DATOSACCESO_EMAIL_EXISTING_EXCEPTION
            ) {
              addWarning(t('validaciones.existing-email'));
            } else if (
              response.data.errorCode ===
              ErrorCodeConstants.UPDATE_DATOSACCESO_EMAIL_CORPORATION
            ) {
              addWarning(t('validaciones.corporative-mail'));
            } else {
              addErrorBack(response.data);
            }
          } else {
            personalData.tipoCambioMail = 'AC'; // Origen cambio de email form update personal data
            DataService.updatePersonalData(
              personalData,
              misDatosHelper.switchTipoUsuario(personalData.tipoUsuario),
            )
              .then(() => {
                if (response.data.error) {
                  addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
                } else {
                  setNewEmail('');
                  // setUserForm(false);
                  // document.body.classList.remove('fix');
                  // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
                  // @ts-ignore
                  document.getElementById('acceso-back').click();
                  cargarData();
                  if (accessEmail !== newEmail) {
                    setTextForModalCambioEmail(
                      t(textCambioEmail1) + t(textCambioEmail2),
                    );
                    setEmailCambiado(true);
                  }
                }
              })
              .catch((er: any) => {
                console.log(er);
                addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
              });
          }
        })
        .catch((er) => {
          console.log(er);
          addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
        });
    }
  };

  const realizarCambio = () => {
    const oldPass =
      dataUser.user.registroSocial === false ? passActual : 'Auto';
    const data = {
      passActual: btoa(CryptoUtil.encryptAes(oldPass)),
      pass: btoa(CryptoUtil.encryptAes(passSugerida)),
      repitPass: btoa(CryptoUtil.encryptAes(passRep)),
      fromApp: dataUser.user.fromApp,
    };
    DataService.updatePassAcceso(data)
      .then((response) => {
        if (response.data.error) {
          addErrorBack(response.data);
        } else {
          reset();
          // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
          // @ts-ignore
          document.getElementById('acceso-back').click();
          cerrarSesion();
        }
      })
      .catch((er) => {
        console.log(er);
        addError(t('nlr-vaya-algo-no-ha-ido-bien'), 'WARNING');
      });
  };

  const updatePass = () => {
    let passValid = true;
    if (passSugerida !== passRep) {
      passValid = false;
      addWarning(t('no-equal-password'));
    } else if (
      passSugerida.length === 0 ||
      passRep.length === 0 ||
      (passActual.length === 0 && dataUser.user.registroSocial === false)
    ) {
      passValid = false;
      addWarning(t('nlr-vaya-algo-no-ha-ido-bien'));
    } else if (passSugerida.length < 8) {
      passValid = false;
      addWarning(t('pass-length-invalid'));
    } else if (!SemaforoHelper.cumpleMinimos(passSugerida)) {
      passValid = false;
      addWarning(t('pass-invalid-title'));
    }
    if (passValid) {
      setPasswordCambiada(true);
    }
  };

  useEffect(() => {
    setLoad(true);
  }, [dataUser.user.token]);

  useEffect(() => {
    triggerValidation('repNewPsw');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passSugerida]);

  //* *** Save with Enter key
  const saveInfo = (e) => {
    if (e.key === 'Enter' && e.keyCode === 13) {
      if (userForm) {
        const element = document.querySelector(
          'section.o-section--large.email-form.bloque-form.visible',
        ) as HTMLElement;
        element.removeEventListener('keydown', saveInfo, false);
      } else {
        const element = document.querySelector(
          'section.o-section--large.form-acceso.bloque-form.visible',
        ) as HTMLElement;
        element.removeEventListener('keydown', saveInfo, false);
      }

      e.preventDefault();
      e.stopPropagation();
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      document.getElementById('datos-acceso-submit-btn').click();
    }
  };

  useEffect(() => {
    if (userForm && load) {
      const element = document.querySelector(
        'section.o-section--large.email-form.bloque-form.visible',
      ) as HTMLElement;

      element.setAttribute('tabindex', '0');
      element.focus();
      element.addEventListener('keydown', saveInfo, false);
    }
    if (passForm && load) {
      const element = document.querySelector(
        'section.o-section--large.form-acceso.bloque-form.visible',
      ) as HTMLElement;

      element.setAttribute('tabindex', '0');
      element.focus();
      element.addEventListener('keydown', saveInfo, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userForm, passForm, load]);

  return (
    <>
      {(() => {
        if (load) {
          return (
            <>
              <section
                id="section"
                className={`o-section--large ${
                  passForm ? 'form-acceso' : 'email-form'
                } bloque-form ${userForm || passForm ? 'visible' : ''}`}
              >
                <div className="container">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                  <a
                    id="acceso-back"
                    href="#acceso"
                    onClick={() => {
                      if (userForm) {
                        setUserForm(false);
                        document.body.classList.remove('fix');
                      } else if (passForm) {
                        setPassSugerida('');
                        setPassActual('');
                        setPassRep('');
                        setPassForm(false);
                        document.body.classList.remove('fix');
                      }
                    }}
                    className="link-prev"
                  >
                    <div className="icon icon-medium">
                      <IconBack />
                    </div>
                    <p className="u-font-normal u-text-brand u-margin-left">
                      {t('datos.accessdata.back')}{' '}
                    </p>
                  </a>
                  <form
                    id="form-datos-usuario"
                    className="form-datos"
                    onSubmit={handleSubmit(() => {
                      if (userForm) {
                        updateEmail();
                      } else if (passForm) {
                        updatePass();
                      }
                    })}
                  >
                    <fieldset>
                      <h2 className="bloque-datos__title u-text-brand">
                        <span className="divider__square brand-color" />
                        <span className="u-margin-right">
                          {t('datos.accessdata.update-access-data')}
                        </span>
                      </h2>
                      {userForm ? (
                        <>
                          <p className="u-margin-bottom">
                            {t('datos.accessdata.label-actual-email')}{' '}
                            <strong>{accessEmail}</strong>
                          </p>
                          <div className="row u-justify-content-space-between">
                            <div className="col-12 col-sm-5">
                              <div className="form-group u-margin-bottom-large">
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label
                                  htmlFor="usuario"
                                  className="control-label"
                                >
                                  {t('datos.accessdata.label-new-email')}
                                </label>
                                <div className="form-control-block">
                                  <input
                                    type="email"
                                    className={`form-control ${
                                      errors.usuario ? 'error' : ''
                                    }`}
                                    name="usuario"
                                    id="usuario"
                                    maxLength={
                                      MisDatosConstants.MAX_LENGTH_EMAIL
                                    }
                                    onChange={(e) =>
                                      HandleChange('newEmail', e)
                                    }
                                    ref={register({
                                      required: true,
                                      // eslint-disable-next-line no-useless-escape
                                      pattern: misDatosHelper.getExpresionRegularEmail(),
                                      validate: {
                                        valid: (value) =>
                                          misDatosHelper.isAsciiPrintableEmail(
                                            value,
                                          ),
                                        corporativeMail: (value) =>
                                          misDatosHelper.isCorporativeEmail(
                                            value,
                                          ),
                                      },
                                    })}
                                  />
                                  {errors.usuario &&
                                    errors.usuario.type === 'required' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="usuario-error"
                                        className="error"
                                        htmlFor="usuario"
                                      >
                                        {t('validaciones.empty-value')}
                                      </label>
                                    )}
                                  {errors.usuario &&
                                    errors.usuario.type === 'pattern' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="usuario-error"
                                        className="error"
                                        htmlFor="usuario"
                                      >
                                        {t('validaciones.not-valid-email')}
                                      </label>
                                    )}
                                  {errors.usuario &&
                                    errors.usuario.type === 'valid' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="usuario-error"
                                        className="error"
                                        htmlFor="usuario"
                                      >
                                        {t('validaciones.not-valid-value')}
                                      </label>
                                    )}
                                  {errors.usuario &&
                                    errors.usuario.type ===
                                      'corporativeMail' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="usuario-error"
                                        className="error"
                                        htmlFor="usuario"
                                      >
                                        {t('validaciones.corporative-mail')}
                                      </label>
                                    )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        ''
                      )}
                      {passForm ? (
                        <>
                          <div className="row u-justify-content-space-between">
                            <div className="col-12 col-sm-5">
                              {(() => {
                                if (dataUser.user.registroSocial === false) {
                                  return (
                                    <>
                                      <div className="form-group u-margin-bottom">
                                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                        <label
                                          htmlFor="pswActual"
                                          className="control-label"
                                        >
                                          {t(
                                            'datos.accessdata.label-actual-pass',
                                          )}
                                        </label>
                                        <div className="form-control-block">
                                          <input
                                            type={`${
                                              showPswAct ? 'text' : 'password'
                                            }`}
                                            className={`form-control ${
                                              errors.pswActual ? 'error' : ''
                                            }`}
                                            name="pswActual"
                                            id="pswActual"
                                            maxLength={
                                              MisDatosConstants.MAX_LENGTH_PASSWORD
                                            }
                                            data-rule-required="true"
                                            data-msg-required="Por favor introduce una contraseña"
                                            onChange={(e) =>
                                              HandleChange('pswActual', e)
                                            }
                                            ref={register({
                                              required: !dataUser.user
                                                .registroSocial,
                                            })}
                                          />
                                          {errors.pswActual &&
                                            errors.pswActual.type ===
                                              'required' && (
                                              // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                              <label
                                                id="pswActual-error"
                                                className="error"
                                                htmlFor="pswActual"
                                              >
                                                {t('validaciones.empty-value')}
                                              </label>
                                            )}
                                          <div className="icon icon-small eye">
                                            <IconEye
                                              onClick={() => {
                                                setShowPswRep(!showPswRep);
                                                setShowPswAct(!showPswAct);
                                                setShowPswNew(!showPswNew);
                                              }}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </>
                                  );
                                }
                                return false;
                              })()}
                              <div className="form-group u-margin-bottom">
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label htmlFor="psw" className="control-label">
                                  {t('datos.accessdata.label-new-pass')}
                                </label>
                                <div className="form-control-block">
                                  <input
                                    type={`${showPswNew ? 'text' : 'password'}`}
                                    className={`form-control ${
                                      errors.psw ? 'error' : ''
                                    }`}
                                    name="psw"
                                    id="psw"
                                    minLength={
                                      MisDatosConstants.MIN_LENGTH_PASSWORD
                                    }
                                    maxLength={
                                      MisDatosConstants.MAX_LENGTH_PASSWORD
                                    }
                                    data-rule-required="true"
                                    data-msg-required="Por favor introduce una contraseña"
                                    onChange={(e) => HandleChange('newPsw', e)}
                                    ref={register({
                                      required: true,
                                      minLength: 8,
                                    })}
                                  />
                                  {errors.psw &&
                                    errors.psw.type === 'required' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="psw-error"
                                        className="error"
                                        htmlFor="psw"
                                      >
                                        {t('validaciones.empty-value')}
                                      </label>
                                    )}
                                  {errors.psw &&
                                    errors.psw.type === 'minLength' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="psw-error"
                                        className="error"
                                        htmlFor="psw"
                                      >
                                        {t(
                                          'validaciones.not-valid-amount-charater-password',
                                        )}
                                      </label>
                                    )}
                                  <div className="icon icon-small eye">
                                    <IconEye
                                      onClick={() => {
                                        setShowPswRep(!showPswRep);
                                        setShowPswAct(!showPswAct);
                                        setShowPswNew(!showPswNew);
                                      }}
                                    />
                                  </div>
                                </div>
                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid,jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                                <a
                                  className="link u-margin-top-small"
                                  onClick={() => {
                                    getPassSugerida();
                                  }}
                                >
                                  {t('datos.accessdata.suggest-pass')}
                                </a>
                              </div>

                              <div className="form-group u-margin-bottom">
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label
                                  htmlFor="repNewPsw"
                                  className="control-label"
                                >
                                  {t('datos.accessdata.repite-pass')}
                                </label>
                                <div className="form-control-block">
                                  <input
                                    type={`${showPswRep ? 'text' : 'password'}`}
                                    className={`form-control ${
                                      errors.repNewPsw ? 'error' : ''
                                    }`}
                                    name="repNewPsw"
                                    id="repNewPsw"
                                    minLength={
                                      MisDatosConstants.MIN_LENGTH_PASSWORD
                                    }
                                    maxLength={
                                      MisDatosConstants.MAX_LENGTH_PASSWORD
                                    }
                                    data-rule-required="true"
                                    data-msg-required="Por favor introduce una contraseña"
                                    onPaste={(e) => e.preventDefault()}
                                    onChange={(e) =>
                                      HandleChange('repNewPsw', e)
                                    }
                                    ref={register({
                                      required: true,
                                      validate: (value) => validarRePass(value),
                                    })}
                                  />
                                  {errors.repNewPsw &&
                                    errors.repNewPsw.type === 'required' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="repNewPsw-error"
                                        className="error"
                                        htmlFor="repNewPsw"
                                      >
                                        {t('validaciones.empty-value')}
                                      </label>
                                    )}
                                  {errors.repNewPsw &&
                                    errors.repNewPsw.type === 'validate' && (
                                      // eslint-disable-next-line jsx-a11y/label-has-associated-control
                                      <label
                                        id="repNewPsw-error"
                                        className="error"
                                        htmlFor="repNewPsw"
                                      >
                                        {t(
                                          'validaciones.not-valid-repeated-password',
                                        )}
                                      </label>
                                    )}
                                  <div className="icon icon-small eye">
                                    <IconEye
                                      onClick={() => {
                                        setShowPswRep(!showPswRep);
                                        setShowPswAct(!showPswAct);
                                        setShowPswNew(!showPswNew);
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-sm-5">
                              <div className="u-margin-bottom-xlarge">
                                <div className="requisitos u-margin-bottom-large">
                                  <h2 className="u-margin-bottom">
                                    {t('datos.accessdata.pass-requirements')}
                                  </h2>
                                  <ul className="lista-requisitos">
                                    <li id="cant-characters">
                                      {t('datos.accessdata.cant-characters')}
                                    </li>
                                    <li id="up-less-letters">
                                      {t('datos.accessdata.up-less-letters')}
                                    </li>
                                    <li id="not-include-equal-letters">
                                      {t(
                                        'datos.accessdata.not-include-equal-letters',
                                      )}
                                    </li>
                                    <li id="not-easy-recognise">
                                      {' '}
                                      {t('datos.accessdata.not-easy-recognise')}
                                    </li>
                                  </ul>
                                  <br />
                                  <h2 className="u-margin-bottom">
                                    {t('datos.accessdata.recomendation-title')}
                                  </h2>
                                  <ul className="lista-requisitos">
                                    <li id="numbers-recomendation">
                                      {t(
                                        'datos.accessdata.recomendation-numbers',
                                      )}
                                    </li>
                                    <li id="specials-recomendatios">
                                      {t(
                                        'datos.accessdata.recomendation-specials',
                                      )}
                                    </li>
                                  </ul>
                                </div>
                                <div className="barra-seguridad">
                                  <p>
                                    {t('datos.accessdata.level-security')}:{' '}
                                    <span id="level-span" className="nivel">
                                      {t('global.vacio')}
                                    </span>
                                  </p>

                                  <div
                                    id="security_level"
                                    className="barra barra__vacio"
                                  >
                                    <span className="barra__nivel" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        ''
                      )}

                      <div className="u-text-right">
                        <button
                          id="datos-acceso-submit-btn"
                          type="submit"
                          className="btn btn-primary "
                        >
                          {t('datos.accessdata.update')}
                        </button>
                      </div>
                    </fieldset>
                  </form>
                </div>
              </section>
              <ModalConfirmacion
                isBig={false}
                isSmall={false}
                textCentered
                showModal={emailCambiado}
                setShowModal={setEmailCambiado}
                title={titleCambioEmail}
                textHTMLContent={textForModalCambioEmail}
                doAceptar={emptymethod}
                doClose={emptymethod}
                showCancel={false}
                acceptButtonTextKey={acceptButtonText}
              />
              <ModalConfirmacion
                isBig={false}
                isSmall
                textCentered
                showModal={passwordCambiada}
                setShowModal={setPasswordCambiada}
                title={titleCambioPassword}
                textHTMLContent={textCambioPassword}
                doAceptar={realizarCambio}
                doClose={emptymethod}
                showCancel
                acceptButtonTextKey={acceptButtonText}
              />
            </>
          );
        }
        return false;
      })()}
    </>
  );
};

export default AccesoForm;
